import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

interface SettingsState {
  theme: boolean;
  mpin: string;
  language: string;
  timeZone: string;
}

const initialState: SettingsState = {
  theme: true,
  mpin: "Standard",
  language: localStorage.getItem("appLanguage") || "English",
  timeZone: localStorage.getItem("appTimeZone") || "Set Automatically",
};

// Helper function for logging
const logSettingChange = (
  setting: string,
  oldValue: any,
  newValue: any,
  action?: string,
) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    setting,
    oldValue,
    newValue,
    action: action || "manual_change",
    source: "settingsSlice",
  };

  const existingLogs = JSON.parse(
    localStorage.getItem("settingsChangeLog") || "[]",
  );
  existingLogs.unshift(logEntry);
  if (existingLogs.length > 50) existingLogs.pop();
  localStorage.setItem("settingsChangeLog", JSON.stringify(existingLogs));
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<boolean>) => {
      logSettingChange("theme", state.theme, action.payload);
      state.theme = action.payload;
    },
    setMpin: (state, action: PayloadAction<string>) => {
      logSettingChange("mpin", state.mpin, action.payload);
      state.mpin = action.payload;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      logSettingChange("language", state.language, action.payload);
      state.language = action.payload;
      localStorage.setItem("appLanguage", action.payload);
    },
    setTimeZone: (state, action: PayloadAction<string>) => {
      logSettingChange("timeZone", state.timeZone, action.payload);
      state.timeZone = action.payload;
      localStorage.setItem("appTimeZone", action.payload);
    },

    // Optional: Add a log action to manually trigger logging
    logSettingsState: (state) => {
      const currentState = {
        timestamp: new Date().toISOString(),
        theme: state.theme,
        mpin: state.mpin,
        language: state.language,
        timeZone: state.timeZone,
        source: "manual_trigger",
      };
    },
  },
});

export const { setTheme, setMpin, setLanguage, setTimeZone, logSettingsState } =
  settingsSlice.actions;
export default settingsSlice.reducer;
